import React, { useState } from 'react';
import { BookmarkIcon, ExternalLinkIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, MinusSquareIcon, CreditCardIcon, FileSignatureIcon, AlertTriangleIcon, CheckCircleIcon, ClockIcon, XCircleIcon, PaperclipIcon, MessageSquareIcon, TagIcon, EyeIcon, ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
export function DocumentGrid({
  documents,
  bookmarkedDocs,
  onToggleBookmark,
  onDocumentSelect,
  onDocumentOpen,
  isLoading,
  locale = 'en-US'
}) {
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  // Format currency based on locale
  const formatCurrency = amount => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };
  // Format date based on locale
  const formatDate = dateString => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  // Get document type icon
  const getDocumentTypeIcon = typeCode => {
    switch (typeCode) {
      case 'INV':
        return <ReceiptIcon className="h-4 w-4 text-purple-500" data-id="element-5059" />;
      case 'PO':
        return <ShoppingCartIcon className="h-4 w-4 text-blue-500" data-id="element-5060" />;
      case 'REQ':
        return <FileTextIcon className="h-4 w-4 text-green-500" data-id="element-5061" />;
      case 'CN':
        return <MinusSquareIcon className="h-4 w-4 text-red-500" data-id="element-5062" />;
      case 'EXP':
        return <CreditCardIcon className="h-4 w-4 text-yellow-500" data-id="element-5063" />;
      case 'CTR':
        return <FileSignatureIcon className="h-4 w-4 text-indigo-500" data-id="element-5064" />;
      default:
        return <FileTextIcon className="h-4 w-4 text-gray-500" data-id="element-5065" />;
    }
  };
  // Get status badge type
  const getStatusBadgeType = status => {
    switch (status) {
      case 'Approved':
      case 'Completed':
      case 'Paid':
      case 'Active':
      case 'Processed':
        return 'success';
      case 'Pending Approval':
      case 'Sent to Vendor':
        return 'info';
      case 'Draft':
        return 'default';
      case 'Exception':
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };
  // Handle sort change
  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort documents based on current sort settings
  const sortedDocuments = [...documents].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'id':
        comparison = a.id.localeCompare(b.id);
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'date':
        comparison = new Date(a.date) - new Date(b.date);
        break;
      case 'amount':
        comparison = a.amount - b.amount;
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'vendor':
        if (a.vendor && b.vendor) {
          comparison = a.vendor.localeCompare(b.vendor);
        } else if (a.vendor) {
          comparison = -1;
        } else if (b.vendor) {
          comparison = 1;
        }
        break;
      case 'entity':
        comparison = a.entity.localeCompare(b.entity);
        break;
      case 'createdBy':
        comparison = a.createdBy.localeCompare(b.createdBy);
        break;
      default:
        comparison = 0;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ChevronUpIcon className="h-4 w-4 ml-1" data-id="element-5066" /> : <ChevronDownIcon className="h-4 w-4 ml-1" data-id="element-5067" />;
  };
  return <div className="overflow-x-auto" data-id="element-5068">
      {isLoading ? <div className="flex justify-center items-center py-20" data-id="element-5069">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-500" data-id="element-5070"></div>
          <span className="ml-3 text-sm text-gray-500" data-id="element-5071">
            Loading documents...
          </span>
        </div> : sortedDocuments.length === 0 ? <div className="text-center py-10" data-id="element-5072">
          <p className="text-gray-500" data-id="element-5073">
            No documents found matching your criteria.
          </p>
        </div> : <table className="min-w-full divide-y divide-gray-200" data-id="element-5074">
          <thead className="bg-gray-50" data-id="element-5075">
            <tr data-id="element-5076">
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-5077">
                <span className="sr-only" data-id="element-5078">Bookmark</span>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('id')} data-id="element-5079">
                <div className="flex items-center" data-id="element-5080">
                  ID {renderSortIndicator('id')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('type')} data-id="element-5081">
                <div className="flex items-center" data-id="element-5082">
                  Type {renderSortIndicator('type')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('date')} data-id="element-5083">
                <div className="flex items-center" data-id="element-5084">
                  Date {renderSortIndicator('date')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('amount')} data-id="element-5085">
                <div className="flex items-center" data-id="element-5086">
                  Amount {renderSortIndicator('amount')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')} data-id="element-5087">
                <div className="flex items-center" data-id="element-5088">
                  Status {renderSortIndicator('status')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('vendor')} data-id="element-5089">
                <div className="flex items-center" data-id="element-5090">
                  Vendor/Entity {renderSortIndicator('vendor')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('createdBy')} data-id="element-5091">
                <div className="flex items-center" data-id="element-5092">
                  Created By {renderSortIndicator('createdBy')}
                </div>
              </th>
              <th scope="col" className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5093">
                Info
              </th>
              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-5094">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-5095">
            {sortedDocuments.map(document => <tr key={document.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => onDocumentSelect(document)} data-id="element-5096">
                <td className="px-3 py-4 whitespace-nowrap" onClick={e => {
            e.stopPropagation();
            onToggleBookmark(document.id);
          }} data-id="element-5097">
                  <BookmarkIcon className={`h-5 w-5 ${bookmarkedDocs.includes(document.id) ? 'text-yellow-500 fill-current' : 'text-gray-300'}`} data-id="element-5098" />
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-5099">
                  <div className="flex items-center" data-id="element-5100">
                    {getDocumentTypeIcon(document.typeCode)}
                    <span className="ml-2 text-sm font-medium text-gray-900" data-id="element-5101">
                      {document.id}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-5102">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-5103">
                    {document.typeCode}
                  </span>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5104">
                  {formatDate(document.date)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-5105">
                  {formatCurrency(document.amount)}
                </td>
                <td className="px-3 py-4 whitespace-nowrap" data-id="element-5106">
                  <StatusBadge status={getStatusBadgeType(document.status)} text={document.status} data-id="element-5107" />
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5108">
                  <div data-id="element-5109">{document.vendor || 'N/A'}</div>
                  <div className="text-xs text-gray-400" data-id="element-5110">
                    {document.entity} - {document.site}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5111">
                  <div data-id="element-5112">{document.createdBy}</div>
                  <div className="text-xs text-gray-400" data-id="element-5113">
                    {formatDate(document.createdDate)}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-5114">
                  <div className="flex space-x-1" data-id="element-5115">
                    {document.hasAttachments && <span title={`${document.attachmentCount} attachment(s)`} data-id="element-5116">
                        <PaperclipIcon className="h-4 w-4 text-gray-400" data-id="element-5117" />
                      </span>}
                    {document.hasNotes && <span title={`${document.noteCount} note(s)`} data-id="element-5118">
                        <MessageSquareIcon className="h-4 w-4 text-gray-400" data-id="element-5119" />
                      </span>}
                    {document.hasTags && <span title={document.tags.join(', ')} data-id="element-5120">
                        <TagIcon className="h-4 w-4 text-gray-400" data-id="element-5121" />
                      </span>}
                    {document.hasWarnings && <span title="Has warnings" data-id="element-5122">
                        <AlertTriangleIcon className="h-4 w-4 text-yellow-500" data-id="element-5123" />
                      </span>}
                    {document.hasErrors && <span title="Has errors" data-id="element-5124">
                        <XCircleIcon className="h-4 w-4 text-red-500" data-id="element-5125" />
                      </span>}
                  </div>
                </td>
                <td className="px-3 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-5126">
                  <div className="flex justify-end space-x-2" data-id="element-5127">
                    <button onClick={e => {
                e.stopPropagation();
                onDocumentSelect(document);
              }} className="text-indigo-600 hover:text-indigo-900" data-id="element-5128">
                      <EyeIcon className="h-4 w-4" data-id="element-5129" />
                    </button>
                    <button onClick={e => {
                e.stopPropagation();
                onDocumentOpen(document);
              }} className="text-indigo-600 hover:text-indigo-900" data-id="element-5130">
                      <ExternalLinkIcon className="h-4 w-4" data-id="element-5131" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>}
    </div>;
}